import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { Redirect, Route, Switch } from 'react-router-dom';
import classNames from 'classnames';
import type { IProps } from './setting-container';
import SettingNavigation from './components/setting-navigation';
import hasPermission from '../../shared/utils/access-control/has-permission';
import { Permissions } from '../../shared/utils/access-control/enums/permissions';
import Suspense from '../../shared/components/suspense';
import ErrorBoundaryWrapper from '../../shared/components/error-boundary-wrapper';
import retryLazy from '../retry-lazy';
import { Routes } from '../../shared/routes';
import { GlobalSettings } from '../../shared/components/global-settings-wrapper';
import { getGlobalSettingsWrapperCustomClassName } from './helpers/helper';
import history from '../../shared/history';

const MyProfile = React.lazy(() =>
  retryLazy(
    () =>
      import(/* webpackChunkName: "my-profile" */ './components/my-profile'),
  ),
);
const UsersAndTeams = React.lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "users-and-teams" */ './components/users-and-teams'
      ),
  ),
);
const Schedule = React.lazy(() =>
  retryLazy(
    () => import(/* webpackChunkName: "schedule" */ './components/schedule'),
  ),
);
const OutOfOffice = React.lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "out-of-office" */ './components/out-of-office'
      ),
  ),
);
const CustomField = React.lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "custom-fields" */ './components/custom-fields'
      ),
  ),
);
const CustomOutcome = React.lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "custom-outcomes" */ './components/custom-outcomes'
      ),
  ),
);
const CallOutcome = React.lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "call-outcomes" */ './components/call-outcomes'
      ),
  ),
);
const CustomDomain = React.lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "custom-domain" */ './components/custom-domain'
      ),
  ),
);
const AdminSettings = React.lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "admin-settings" */ './components/admin-settings'
      ),
  ),
);
const DoNotContactList = React.lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "do-not-contact" */ './components/do-not-contact'
      ),
  ),
);
const Integrations = React.lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "integrations" */ './components/integrations'
      ),
  ),
);
const APITokens = React.lazy(() =>
  retryLazy(
    () =>
      import(/* webpackChunkName: "api-tokens" */ './components/api-tokens'),
  ),
);
const Webhook = React.lazy(() =>
  retryLazy(
    () => import(/* webpackChunkName: "webhook" */ './components/webhook'),
  ),
);
const Whitelabel = React.lazy(() =>
  retryLazy(
    () =>
      import(/* webpackChunkName: "whitelabel" */ './components/whitelabel'),
  ),
);
const McpSettings = React.lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "mcp-settings" */ './components/mcp-settings'
      ),
  ),
);
const DoNotCallList = React.lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "do-not-call" */ './components/do-not-call'
      ),
  ),
);

const Setting: React.FC<IProps> = () => {
  const route = history?.location?.pathname;

  const settingsNavigationClassName = classNames([
    'settings-navigation',
    {
      'settings-navigation__hidden':
        route.includes(Routes.Setting_Billing_Upgrade_Lead_Finder_Plan) ||
        route.includes(Routes.Setting_Billing_Upgrade_Email_Outreach_Plan) ||
        route.includes(Routes.Setting_Billing_Inbox_Placement_Radar) ||
        route.includes(Routes.Setting_Billing_Upgrade_Dialer_Plan),
    },
  ]);

  const settingComponentsClassName = classNames([
    'setting-components',
    {
      'setting-components-nav-hidden':
        route.includes(Routes.Setting_Billing_Upgrade_Lead_Finder_Plan) ||
        route.includes(Routes.Setting_Billing_Upgrade_Email_Outreach_Plan) ||
        route.includes(Routes.Setting_Billing_Inbox_Placement_Radar) ||
        route.includes(Routes.Setting_Billing_Upgrade_Dialer_Plan),
      'settings-users-and-teams-tab': route.includes('/settings/users'),
    },
  ]);

  return (
    <Container fluid className="setting-container">
      <Row className="h-100">
        <Col className="h-100">
          <Row className="setting-content">
            <div className={settingsNavigationClassName}>
              <SettingNavigation />
            </div>
            <div className={settingComponentsClassName}>
              <ErrorBoundaryWrapper>
                <Suspense>
                  <Switch>
                    <Route
                      exact
                      path="/settings/email-accounts"
                      render={() => <Redirect to="/email-accounts" />}
                    />
                    <Route
                      render={({ location }) => (
                        <GlobalSettings.Wrapper
                          className={getGlobalSettingsWrapperCustomClassName()}
                        >
                          <Switch location={location}>
                            <Route
                              path="/settings/profile"
                              component={MyProfile}
                            />
                            <Route
                              path="/settings/custom-fields"
                              component={CustomField}
                            />
                            <Route
                              path="/settings/custom-outcomes"
                              component={CustomOutcome}
                            />
                            <Route
                              path="/settings/call-outcomes"
                              component={CallOutcome}
                            />
                            <Route
                              path="/settings/custom-domain"
                              component={CustomDomain}
                            />
                            <Route
                              path="/settings/schedules"
                              component={Schedule}
                            />
                            <Route
                              path="/settings/out-of-office"
                              component={OutOfOffice}
                            />
                            {(hasPermission(Permissions.ACCOUNT_USER_READ) ||
                              hasPermission(Permissions.TEAM_SHOW)) && (
                              <Route
                                path="/settings/users"
                                component={UsersAndTeams}
                              />
                            )}
                            {hasPermission(Permissions.ACCOUNT_USER_READ) && (
                              <Route
                                path="/settings/admin-settings"
                                component={AdminSettings}
                              />
                            )}
                            <Route
                              path="/settings/dnc-list"
                              component={DoNotContactList}
                            />
                            <Route
                              path="/settings/call-dnc-list"
                              component={DoNotCallList}
                            />
                            <Route
                              path="/settings/integrations/:slug?"
                              component={Integrations}
                            />
                            <Route
                              exact
                              path="/settings/whitelabel"
                              component={Whitelabel}
                            />
                            <Route
                              path="/settings/webhook"
                              component={Webhook}
                            />
                            <Route
                              path="/settings/webhook/:webhookId"
                              component={Webhook}
                            />
                            <Route
                              path="/settings/webhook/add"
                              component={Webhook}
                            />
                            <Route
                              path="/settings/api-keys"
                              component={APITokens}
                            />
                            <Route
                              path="/settings/mcp"
                              component={McpSettings}
                            />
                            <Redirect to="/settings/profile" />
                          </Switch>
                        </GlobalSettings.Wrapper>
                      )}
                    />
                  </Switch>
                </Suspense>
              </ErrorBoundaryWrapper>
            </div>
          </Row>
        </Col>
      </Row>
    </Container>
  );
};

export default Setting;
